import { useState, useEffect, useCallback } from 'react';
import versionService from '../services/versionService';

const useVersionCheck = (options = {}) => {
    const {
        enableAutoCheck = true,
        onUpdateAvailable = null,
        onError = null,
        checkOnMount = true
    } = options;

    const [versionInfo, setVersionInfo] = useState({
        isOutdated: false,
        isChecking: false,
        lastCheckTime: null,
        currentVersion: versionService.getCurrentVersionInfo(),
        serverVersion: null,
        error: null
    });

    const [showUpdateModal, setShowUpdateModal] = useState(false);

    // Manual version check function
    const checkVersion = useCallback(async () => {
        setVersionInfo(prev => ({ ...prev, isChecking: true, error: null }));

        try {
            const result = await versionService.checkForUpdates();

            if (result && result.success) {
                setVersionInfo(prev => ({
                    ...prev,
                    isOutdated: result.isOutdated,
                    isChecking: false,
                    lastCheckTime: result.checkTime,
                    serverVersion: result.serverVersion,
                    error: null
                }));

                if (result.isOutdated) {
                    setShowUpdateModal(true);
                    if (onUpdateAvailable) {
                        onUpdateAvailable(result);
                    }
                }
            } else {
                setVersionInfo(prev => ({
                    ...prev,
                    isChecking: false,
                    error: result?.error || 'Version check failed'
                }));

                if (onError) {
                    onError(result?.error || 'Version check failed');
                }
            }
        } catch (error) {
            console.error('Version check error in hook:', error);
            setVersionInfo(prev => ({
                ...prev,
                isChecking: false,
                error: error.message
            }));

            if (onError) {
                onError(error.message);
            }
        }
    }, []); // Remove dependencies to prevent infinite loops    // Callback for when update is available (from periodic checks)
    const handleUpdateAvailable = useCallback((result) => {
        setVersionInfo(prev => ({
            ...prev,
            isOutdated: true,
            serverVersion: result.serverVersion,
            lastCheckTime: result.checkTime
        }));
        setShowUpdateModal(true);

        if (onUpdateAvailable) {
            onUpdateAvailable(result);
        }
    }, []); // Remove dependency to prevent infinite loops

    // Callback for version check errors (from periodic checks)
    const handleVersionError = useCallback((error) => {
        setVersionInfo(prev => ({
            ...prev,
            error
        }));

        if (onError) {
            onError(error);
        }
    }, []); // Remove dependency to prevent infinite loops

    // Force reload function
    const forceReload = useCallback(() => {
        versionService.forceReload();
    }, []);

    // Dismiss update modal
    const dismissUpdateModal = useCallback(() => {
        setShowUpdateModal(false);
    }, []);

    // Update now function
    const updateNow = useCallback(() => {
        setShowUpdateModal(false);
        forceReload();
    }, [forceReload]);

    // Start/stop periodic checking
    useEffect(() => {
        if (enableAutoCheck) {
            // console.log('Starting automatic version checking');
            versionService.startPeriodicCheck(handleUpdateAvailable, handleVersionError);

            return () => {
                // console.log('Stopping automatic version checking');
                versionService.stopPeriodicCheck();
            };
        }
    }, [enableAutoCheck, handleUpdateAvailable, handleVersionError]);

    // Initial check on mount - disabled by default to prevent login issues
    useEffect(() => {
        if (checkOnMount && enableAutoCheck) {
            // Delay initial check to avoid blocking login
            const timer = setTimeout(() => {
                console.log('Performing initial version check');
                checkVersion();
            }, 5000); // Wait 5 seconds after mount

            return () => clearTimeout(timer);
        }
    }, [checkOnMount, enableAutoCheck]); // Remove checkVersion dependency

    return {
        // Version information
        versionInfo,

        // Update modal state
        showUpdateModal,
        dismissUpdateModal,

        // Actions
        checkVersion,
        forceReload,
        updateNow,

        // Convenience properties
        isOutdated: versionInfo.isOutdated,
        isChecking: versionInfo.isChecking,
        currentVersion: versionInfo.currentVersion,
        serverVersion: versionInfo.serverVersion,
        lastCheckTime: versionInfo.lastCheckTime,
        error: versionInfo.error
    };
};

export default useVersionCheck;